import { computed, inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { StorageRepository } from '@okcargo-apps/shared-domain';
import { ECapabilities, getEmptyIAccount, IAccount } from '@okcargo-apps/shared-domain-ionic';
import { AccountCommandService, AccountQueryService } from '@okcargo-apps/shared-infrastructure-ionic';
import { JwtService } from '@okcargo-apps/shared-infrastructure';
import { enGB, es } from 'date-fns/locale';
import { DateFnsConfigurationService } from 'ngx-date-fns';

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  private readonly accountQueryService = inject(AccountQueryService);
  private readonly jwtService = inject(JwtService);
  private readonly accountCommandService = inject(AccountCommandService);
  private readonly translate = inject(TranslateService);
  private readonly dateFnsConfigurationService = inject(DateFnsConfigurationService);
  private readonly storageService = inject(StorageRepository);

  /** Key of the last known account, used to work offline (the session cannot be verified without it). */
  private readonly ACCOUNT_STORAGE_KEY = 'account';

  private readonly _account = signal<IAccount>(getEmptyIAccount());
  public readonly account = this._account.asReadonly();

  public readonly isAuthenticated = computed<boolean>(() => this.hasCapability(ECapabilities.AUTHENTICATED));

  constructor() {
    // When the session is cleared (logout or a failed token refresh) the account snapshot is
    // dropped too: it must never keep the user as authenticated without credentials.
    this.jwtService.userDataReceived$.subscribe((sessionVerified) => {
      if (sessionVerified) {
        return;
      }

      // Without credentials there is no session to trust, even if it was never verified in this
      // run (e.g. the app starts with an expired session): the stored snapshot must go, or the app
      // would show the menu and load the authenticated data. With credentials present the session is
      // just not verified yet (a cold start), so the snapshot is kept for the offline use.
      if (!this.jwtService.accessToken && !this.jwtService.refreshToken) {
        this.clearIdentity();
      }
    });
  }

  public hasCapability(capability: ECapabilities): boolean {
    return this._account().capabilities.includes(capability);
  }

  /** Verifies the stored session. Returns whether the account could be loaded. */
  public async checkUserAuthentication(): Promise<boolean> {
    try {
      const account = await this.accountQueryService.getIdentity(this._account());

      this.setAccount(account);

      return true;
    } catch (error) {
      console.error('[AccountService] Error on checkUserAuthentication:', error);

      // The session may have been rejected (the interceptor clears the credentials on a 401): the
      // snapshot must not keep the user authenticated then.
      if (!this.jwtService.accessToken && !this.jwtService.refreshToken) {
        this.clearIdentity();
      }

      return false;
    }
  }

  /** Applies the account to the app and keeps the offline snapshot up to date. */
  private setAccount(account: IAccount): void {
    this._account.set(account);
    this.setDateFnsLocale(account.lang);
    void this.storageService.create(this.ACCOUNT_STORAGE_KEY, JSON.stringify(account));
  }

  /**
   * Restores the account stored in the device, so the app can be used without connectivity (the
   * session cannot be verified again until there is connection).
   */
  public async restoreAccountFromStorage(): Promise<void> {
    try {
      const { value } = await this.storageService.read(this.ACCOUNT_STORAGE_KEY);

      if (value) {
        this.setAccount(JSON.parse(value) as IAccount);
      }
    } catch (error) {
      console.warn('[AccountService] Could not restore the stored account:', error);
    }
  }

  public clearIdentity(): void {
    this._account.set(getEmptyIAccount());
    void this.storageService.delete(this.ACCOUNT_STORAGE_KEY);
  }

  public async refreshCompanyData(): Promise<void> {
    try {
      const currentAccount = this._account();

      if (!currentAccount.id) return;

      const updatedCompany = await this.accountQueryService.getCompanyData();

      this._account.update((account) => ({
        ...account,
        company: updatedCompany,
      }));
    } catch (error) {
      console.error('[AccountService] Error on refreshCompanyData:', error);
    }
  }

  public reloadCompanyDataAfterSave(): void {
    setTimeout(() => {
      this.refreshCompanyData();
    }, 2000); // 2 sec
  }

  // TODO maybe move to a separate service, as it's not strictly related to the account, but to the preferences
  public async changeLang(lang: string): Promise<void> {
    this.translate.setFallbackLang(lang);
    this.translate.use(lang);
    this.setDateFnsLocale(lang);
    this.accountCommandService.saveLang(lang).catch((err) => console.error('[AccountService] Error saving lang:', err));
  }

  private setDateFnsLocale(lang: string): void {
    if (lang === 'es') {
      this.dateFnsConfigurationService.setLocale(es);
    } else {
      this.dateFnsConfigurationService.setLocale(enGB);
    }
  }

  // TODO acceptPrivacyPolicy() {
}
